import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = slug ? categoryMap[slug] : undefined;

  const articles = useMemo(() => {
    if (!slug) return [];
    return getArticlesByCategory(slug);
  }, [slug]);

  const catName = activeCategory?.name || '分类';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gxjiaoyan.cn' },
          { '@type': 'ListItem', position: 2, name: catName },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${catName} - 长日光阴`,
        description: activeCategory?.description || '',
        url: `https://gxjiaoyan.cn/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <SeoHead
        title={catName}
        description={activeCategory?.description || `探索长日光阴的${catName}内容，慢生活美学品牌——珍藏每一寸慢时光。`}
        keywords={`长日光阴,${catName},慢生活,生活美学,时光记录`}
        canonicalUrl={`https://gxjiaoyan.cn/category/${slug}`}
      />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-50">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: catName }]} />

          <section className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                {catName}
              </h1>
              {activeCategory && (
                <p className="text-sm text-foreground-500 max-w-2xl leading-relaxed mb-8">
                  {activeCategory.description}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>

        <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/70"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="whitespace-nowrap">{article.author}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span className="whitespace-nowrap">{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span className="whitespace-nowrap">{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}